import React from 'react';
import {
  EnrichmentAlert,
  EnrichmentDataTable,
  EnrichmentField,
  EnrichmentFieldGrid,
  EnrichmentPill,
  EnrichmentSectionIntro,
  EnrichmentSignalList,
  riskTone,
  voipTone,
} from '../primitives';
import GenericSectionBody from './generic';

function PhoneAssignedCarrier({ data }) {
  const porting = data.porting_analysis || {};
  return (
    <>
      <EnrichmentFieldGrid columns={3}>
        <EnrichmentField label="Carrier" value={data.carrier} />
        <EnrichmentField label="Line type" value={data.line_type} />
        <EnrichmentField label="VoIP infrastructure">
          <EnrichmentPill tone={voipTone(data.is_voip)}>
            {data.is_voip ? 'Yes' : 'No'}
          </EnrichmentPill>
        </EnrichmentField>
        <EnrichmentField label="Company type" value={data.company_type} />
        <EnrichmentField label="Rate center" value={data.rate_center} />
        <EnrichmentField label="Region" value={data.region} />
        <EnrichmentField label="Block" value={data.block} mono />
        <EnrichmentField label="OCN" value={data.ocn} mono />
        <EnrichmentField label="LATA" value={data.lata} />
      </EnrichmentFieldGrid>
      {data.voip_detail && (
        <EnrichmentAlert variant="warning">{data.voip_detail}</EnrichmentAlert>
      )}
      {porting.summary && (
        <>
          <EnrichmentSectionIntro>
            Porting likelihood: <strong>{porting.likelihood || 'Unknown'}</strong>
            {porting.port_score != null ? ` (score ${porting.port_score})` : ''}
          </EnrichmentSectionIntro>
          <EnrichmentSignalList items={porting.indicators} />
        </>
      )}
      {data.nxx_carriers?.length > 0 && (
        <EnrichmentDataTable
          columns={[
            { key: 'name', label: 'Carrier' },
            { key: 'type', label: 'Type' },
            { key: 'block', label: 'Block' },
          ]}
          rows={data.nxx_carriers}
          empty="No alternate carriers in block"
        />
      )}
    </>
  );
}

function PhoneLiveCarrier({ data }) {
  return (
    <>
      <EnrichmentFieldGrid columns={3}>
        <EnrichmentField label="Carrier" value={data.carrier} />
        <EnrichmentField label="Line type" value={data.line_type} />
        <EnrichmentField label="VoIP">
          <EnrichmentPill tone={voipTone(data.is_voip)}>
            {data.is_voip ? 'Yes' : 'No'}
          </EnrichmentPill>
        </EnrichmentField>
        <EnrichmentField label="Valid" value={data.valid} />
        <EnrichmentField label="E.164" value={data.e164} mono />
        <EnrichmentField label="National format" value={data.national_format} mono />
        <EnrichmentField label="Country" value={data.country_code} />
        <EnrichmentField label="MCC / MNC" value={[data.mobile_country_code, data.mobile_network_code].filter(Boolean).join(' / ') || '—'} />
      </EnrichmentFieldGrid>
      {data.source && (
        <p className="enrichment-source-line">
          Source: {data.source}{data.cached ? ' (cached)' : ''}
        </p>
      )}
    </>
  );
}

function PhoneConsensus({ data }) {
  return (
    <>
      <EnrichmentSectionIntro>{data.summary}</EnrichmentSectionIntro>
      <EnrichmentFieldGrid columns={3}>
        <EnrichmentField label="Assigned carrier" value={data.assigned_carrier} />
        <EnrichmentField label="Live carrier" value={data.live_carrier || 'Not run'} />
        <EnrichmentField label="VoIP consensus" value={(data.voip_consensus || 'none').replace(/_/g, ' ')} />
        <EnrichmentField label="Confidence" value={data.confidence} />
        <EnrichmentField label="Likely ported" value={data.likely_ported} />
        <EnrichmentField label="Carriers match" value={data.carriers_match == null ? '—' : data.carriers_match ? 'Yes' : 'No'} />
      </EnrichmentFieldGrid>
      {data.voip_summary && (
        <EnrichmentAlert variant={data.assigned_is_voip || data.live_is_voip ? 'warning' : 'info'}>
          {data.voip_summary}
        </EnrichmentAlert>
      )}
      <EnrichmentSignalList items={data.indicators} />
    </>
  );
}

function PhoneVoipAnalysis({ data }) {
  return (
    <>
      <EnrichmentFieldGrid columns={3}>
        <EnrichmentField label="VoIP likelihood" value={data.voip_likelihood} />
        <EnrichmentField label="VoIP score" value={data.voip_score} />
        <EnrichmentField label="Flagged as VoIP">
          <EnrichmentPill tone={voipTone(data.is_voip)}>
            {data.is_voip ? 'Yes' : 'No'}
          </EnrichmentPill>
        </EnrichmentField>
      </EnrichmentFieldGrid>
      {data.indicators?.length > 0 && (
        <ul className="enrichment-bullet-list">
          {data.indicators.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function PhoneRiskAnalysis({ data }) {
  return (
    <>
      <div className="enrichment-score-row">
        <div className="enrichment-score-block">
          <div className="enrichment-field-label">Risk score</div>
          <div className="enrichment-score-value">{data.risk_score ?? '—'}</div>
        </div>
        <div className="enrichment-score-block">
          <div className="enrichment-field-label">Risk level</div>
          <EnrichmentPill tone={riskTone(data.risk_level)}>{data.risk_level || 'unknown'}</EnrichmentPill>
        </div>
        <div className="enrichment-score-block">
          <div className="enrichment-field-label">Signals</div>
          <div className="enrichment-field-value">{data.signal_count ?? data.risk_signals?.length ?? 0}</div>
        </div>
      </div>
      <EnrichmentSignalList items={data.risk_signals} />
    </>
  );
}

function PhoneFormatting({ data }) {
  return (
    <EnrichmentFieldGrid columns={2}>
      <EnrichmentField label="E.164" value={data.e164} mono />
      <EnrichmentField label="Formatted" value={data.formatted || data.national} mono />
      <EnrichmentField label="National number" value={data.national_number} mono />
      <EnrichmentField label="Raw input" value={data.raw_input} mono />
      <EnrichmentField label="Digits" value={data.digits} />
      <EnrichmentField label="Valid length" value={data.valid_length} />
    </EnrichmentFieldGrid>
  );
}

function PhoneGeoInfo({ data }) {
  return (
    <EnrichmentFieldGrid columns={3}>
      <EnrichmentField label="Country" value={data.country} />
      <EnrichmentField label="Region" value={data.region} />
      <EnrichmentField label="State / province" value={data.state_province || data.state} />
      <EnrichmentField label="City / rate center" value={data.city_region || data.city} />
      <EnrichmentField label="Timezone" value={data.timezone || (Array.isArray(data.timezones) ? data.timezones.join(', ') : null)} />
      <EnrichmentField label="Area code" value={data.area_code} mono />
    </EnrichmentFieldGrid>
  );
}

function PhoneNumberVariants({ data }) {
  const variants = data.variants || data.items || (Array.isArray(data) ? data : []);
  if (Array.isArray(variants) && variants.length && typeof variants[0] === 'string') {
    return (
      <ul className="enrichment-bullet-list mono">
        {variants.map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  return (
    <EnrichmentDataTable
      columns={[
        { key: 'type', label: 'Format' },
        { key: 'value', label: 'Value' },
      ]}
      rows={variants}
    />
  );
}

const PHONE_RENDERERS = {
  assigned_carrier: PhoneAssignedCarrier,
  live_carrier: PhoneLiveCarrier,
  carrier_consensus: PhoneConsensus,
  voip_analysis: PhoneVoipAnalysis,
  risk_analysis: PhoneRiskAnalysis,
  formatting: PhoneFormatting,
  geo_info: PhoneGeoInfo,
  number_variants: PhoneNumberVariants,
  number_type: ({ data }) => (
    <EnrichmentFieldGrid columns={2}>
      <EnrichmentField label="Type" value={data.type} />
      <EnrichmentField label="Detail" value={data.detail} fullWidth />
      <EnrichmentField label="Toll-free" value={data.is_toll_free} />
      <EnrichmentField label="Premium" value={data.is_premium} />
    </EnrichmentFieldGrid>
  ),
};

export default function PhoneSectionBody({ sectionKey, section, data }) {
  if (section.status === 'skipped' || section.status === 'error') {
    return <GenericSectionBody section={section} data={data} />;
  }
  const Renderer = PHONE_RENDERERS[sectionKey];
  if (!Renderer) return <GenericSectionBody section={section} data={data} />;
  return <Renderer data={data} section={section} />;
}
